import { useMemo, useState } from 'react';
import type { SearchAllergensParams } from '@lotushealth/lotushealth_js_sdk/schemas';
import { z } from 'zod';

import { useAddPatientAllergyMutation, useSearchAllergensQuery } from '@/entities/rx';

import type { AllergySearchResultModel } from './allergiesTableModel';
import { toAllergySearchResultModel } from './allergiesTableModel';

const allergenSearchTextSchema = z
  .string()
  .trim()
  .min(3, 'Type at least 3 characters to search allergens.');

export interface UseAllergySelectionResult {
  addPatientAllergyMutation: ReturnType<typeof useAddPatientAllergyMutation>;

  searchText: string;
  setSearchText: (text: string) => void;
  isSearchTextValid: boolean;
  searchTextError: string | null;

  searchQuery: ReturnType<typeof useSearchAllergensQuery>;
  searchResults: AllergySearchResultModel[];

  pendingAddAllergenId: number | null;
  addError: string | null;
  addAllergy: (row: AllergySearchResultModel) => Promise<void>;
}

export function useAllergySelection(userId: string): UseAllergySelectionResult {
  const addPatientAllergyMutation = useAddPatientAllergyMutation();

  const [searchText, setSearchText] = useState('');
  const [pendingAddAllergenId, setPendingAddAllergenId] = useState<number | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const trimmedSearchText = searchText.trim();
  const searchValidation = useMemo(
    () => allergenSearchTextSchema.safeParse(trimmedSearchText),
    [trimmedSearchText]
  );
  const isSearchTextValid = searchValidation.success;
  const searchTextError = searchValidation.success
    ? null
    : (searchValidation.error.issues[0]?.message ?? null);

  const searchParams = useMemo((): SearchAllergensParams | undefined => {
    if (!isSearchTextValid) {
      return undefined;
    }
    return { name: trimmedSearchText, page_number: 1 };
  }, [isSearchTextValid, trimmedSearchText]);

  const searchQuery = useSearchAllergensQuery(searchParams);

  const searchResults = useMemo((): AllergySearchResultModel[] => {
    const items = searchQuery.data?.Items;
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((r) => toAllergySearchResultModel(r));
  }, [searchQuery.data]);

  const addAllergy = async (row: AllergySearchResultModel) => {
    setAddError(null);

    if (row.allergenId == null) {
      setAddError('Could not add allergy. Missing allergen id.');
      return;
    }

    try {
      setPendingAddAllergenId(row.allergenId);
      await addPatientAllergyMutation.mutateAsync({
        userId,
        data: {
          AllergenId: row.allergenId,
          ReactionType: 'Allergy',
          StatusType: 'Active',
        },
      });

      setSearchText('');
    } catch {
      setAddError('Could not add allergy. Please try again.');
    } finally {
      setPendingAddAllergenId(null);
    }
  };

  return {
    addPatientAllergyMutation,
    searchText,
    setSearchText,
    isSearchTextValid,
    searchTextError,
    searchQuery,
    searchResults,
    pendingAddAllergenId,
    addError,
    addAllergy,
  };
}
